/**
 * Side-by-side comparison: the same body several times over, each showing a
 * different layer state.
 *
 * Why this is cheap: every copy is an instance set over one shared geometry
 * store, so N bodies cost roughly 1x the memory. The runtime's run merge draws
 * each copy as about one sub-draw per system (14 on this atlas), because the
 * instances within a copy share a matrix and a colour but copies do not share
 * one with each other. Ten bodies is therefore ~140 sub-draws, well under the
 * ~2,200 that measured 5 ms on a Quest 3. Triangles are the real budget, not
 * draws: see docs/02-QUEST_PERFORMANCE.md.
 */
import type { Catalog } from "../model.js";
import type { LayerPresetId } from "./visibility.js";
import type { BodyTransform, Vec3 } from "./two-hand-transform.js";

export interface CompareSlot {
  /** Stable id, so a slot keeps its atlas instance across relayouts. */
  id: string;
  preset: LayerPresetId;
  label: string;
  transform: BodyTransform;
}

export interface CompareOptions {
  /** Gap between neighbouring bodies, as a fraction of body width. */
  gap?: number;
  /** Uniform scale for every body in the row. */
  scale?: number;
  /** Where the row is centred. */
  centre?: Vec3;
  /** Which way the row faces; the bodies all face this way. */
  yaw?: number;
}

/** Width of the atlas at the hips, from the catalogue's own bounds. */
export function bodyWidth(catalog: Catalog): number {
  const [min, max] = catalog.bounds;
  return Math.max(0.2, (max[0] ?? 0.35) - (min[0] ?? -0.35));
}

/**
 * Lay `presets` out in a row, centred on `centre`, evenly spaced and all facing
 * the same way. The row grows about its middle, so adding a fourth body does
 * not shove the first three sideways out of view.
 */
export function compareRow(catalog: Catalog, presets: readonly { id: string; preset: LayerPresetId; label: string }[], options: CompareOptions = {}): CompareSlot[] {
  const scale = options.scale ?? 1;
  const gap = options.gap ?? 0.6;
  const centre = options.centre ?? { x: 0, y: 0, z: 0 };
  const yaw = options.yaw ?? 0;
  const pitch = bodyWidth(catalog) * scale * (1 + gap);
  const span = (presets.length - 1) * pitch;

  return presets.map((entry, i) => {
    const offset = -span / 2 + i * pitch;
    // place along the row's own left-right axis, which yaw turns
    const c = Math.cos(yaw);
    const s = Math.sin(yaw);
    return {
      id: entry.id,
      preset: entry.preset,
      label: entry.label,
      transform: {
        position: { x: centre.x + offset * c, y: centre.y, z: centre.z - offset * s },
        scale,
        yaw,
      },
    };
  });
}

/** The comparison the entry page shows: the whole body with its skin, the same without, and the skeleton. */
export const DEFAULT_COMPARISON: readonly { id: string; preset: LayerPresetId; label: string }[] = [
  { id: "cmp-full", preset: "full", label: "Full body" },
  { id: "cmp-noskin", preset: "no-skin", label: "Without skin" },
  { id: "cmp-bones", preset: "bones", label: "Bones" },
];

/**
 * Triangles a comparison draws, so the caller can refuse a row that will not
 * hold frame rate rather than finding out on the headset.
 */
export function comparisonTriangles(catalog: Catalog, presets: readonly { preset: LayerPresetId }[], trianglesFor: (preset: LayerPresetId) => number): number {
  return presets.reduce((total, p) => total + trianglesFor(p.preset), 0);
}
